import { MediaImageQrCode, QrCodeFieldConfig, QrVcardFieldKey } from 'editor/src/store/design/types';

import resolveQrFieldSource, { GetColumnValue, GetLinkedText } from './resolveQrFieldSource';
import { QrVcardData, QrVcardPhone } from './types';

/** All configurable vCard fields, in the order they are shown in the seller mapping UI. */
export const QR_VCARD_FIELD_KEYS: QrVcardFieldKey[] = [
  'firstName',
  'lastName',
  'title',
  'organization',
  'phoneCell',
  'phoneWork',
  'email',
  'url',
  'linkedin',
  'address',
];

/** Field labels (translation keys — pass through t()). */
export const QR_VCARD_FIELD_LABELS: Record<QrVcardFieldKey, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  title: 'Job title',
  organization: 'Company',
  phoneCell: 'Mobile phone',
  phoneWork: 'Work phone',
  email: 'Email',
  url: 'Website',
  linkedin: 'LinkedIn',
  address: 'Address',
};

/** Default vCard TEL TYPE mapping for the phone fields (overridable per field, GCIN-3610 Story 3). */
export const QR_VCARD_DEFAULT_TEL_TYPES: Partial<Record<QrVcardFieldKey, string>> = {
  phoneCell: 'CELL',
  phoneWork: 'WORK',
};

export function defaultQrCodeConfig(): MediaImageQrCode {
  return { type: 'vcard', fields: [], artworkOnly: true };
}

/**
 * Resolves the seller-configured field mapping into the QrVcardData consumed by
 * buildQrPayload. Element-linked fields read the current text of the linked
 * personalizable element via `getLinkedText`; static fields use their fixed value.
 * Only mapped fields are encoded, so the seller's per-client field selection is
 * always respected (GCIN-3610 Story 2).
 */
export function buildVcardFromConfig(
  fields: QrCodeFieldConfig[],
  getLinkedText: GetLinkedText,
  getColumnValue?: GetColumnValue,
): QrVcardData {
  const vcard: QrVcardData = { type: 'vcard' };
  const phones: QrVcardPhone[] = [];

  fields.forEach((field) => {
    const value = resolveQrFieldSource(field.source, getLinkedText, getColumnValue)?.trim();
    if (!value) {
      return;
    }
    switch (field.key) {
      case 'phoneCell':
      case 'phoneWork':
        phones.push({ number: value, telType: field.telType ?? QR_VCARD_DEFAULT_TEL_TYPES[field.key] });
        break;
      case 'firstName':
        vcard.firstName = value;
        break;
      case 'lastName':
        vcard.lastName = value;
        break;
      case 'title':
        vcard.title = value;
        break;
      case 'organization':
        vcard.organization = value;
        break;
      case 'email':
        vcard.email = value;
        break;
      case 'url':
        vcard.url = value;
        break;
      case 'linkedin':
        vcard.linkedin = value;
        break;
      case 'address':
        vcard.address = value;
        break;
      default:
        break;
    }
  });

  if (phones.length) {
    vcard.phones = phones;
  }

  // Designs often carry a single "Name" field. When it is mapped to firstName and no
  // lastName is mapped, split the value so the vCard N: line stays well-formed.
  if (vcard.firstName && !vcard.lastName && !fields.some((field) => field.key === 'lastName')) {
    const parts = vcard.firstName.split(/\s+/);
    if (parts.length > 1) {
      [vcard.firstName] = parts;
      vcard.lastName = parts.slice(1).join(' ');
    }
  }
  return vcard;
}

/** Whether the resolved vCard has any data worth rendering into a QR code. */
export function hasVcardValues(vcard: QrVcardData): boolean {
  return Boolean(
    vcard.firstName ||
      vcard.lastName ||
      vcard.title ||
      vcard.organization ||
      vcard.phones?.length ||
      vcard.email ||
      vcard.url ||
      vcard.linkedin ||
      vcard.address,
  );
}
